import React, { useEffect, useState } from 'react';
import './CMSManager.css';
import Sidebar from '../components/Sidebar';
import { fetchGraphQL } from '../graphql/api/bannerApi';
import { GET_CMS_PAGES, UPDATE_CMS_PAGE, CREATE_CMS_PAGE, DELETE_CMS_PAGE } from '../graphql/bannerGQL';
import AlertMessage from '../components/AlertMessage';
import Loading from '../components/Loading';
import NoData from '../components/NoData';

interface CMSPage {
  id: string;
  title: string;
  content: string;
}

const CMSManager: React.FC = () => {
  const [pages, setPages] = useState<CMSPage[]>([]);
  const [selectedPage, setSelectedPage] = useState<CMSPage | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState<'success' | 'error' | 'info' | 'warning'>('info');
  const [isLoading, setIsLoading] = useState(true);

  const loadPages = () => {
    setIsLoading(true);
    setAlertMessage('Loading pages...');
    setAlertType('info');
    fetchGraphQL(GET_CMS_PAGES)
      .then(data => setPages(data.listCMSPages))
      .catch(err => {
        console.error(err);
        setAlertMessage('Failed to load pages.');
        setAlertType('error');
      });
    setIsLoading(false);
  };

  useEffect(() => {
    loadPages();
  }, []);

  const handleSelect = (page: CMSPage) => {
    setSelectedPage({ ...page });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!selectedPage) return;
    setSelectedPage({
      ...selectedPage,
      [e.target.name]: e.target.value,
    });
  };

  const handleSave = async () => {
    if (!selectedPage) return;

    if (!selectedPage.title.trim() || !selectedPage.content.trim()) {
      setAlertMessage('Title and content are required.');
      setAlertType('warning');
      return;
    }

    setIsSaving(true);

    try {
      if (selectedPage.id === 'new') {
        // Create new page
        await fetchGraphQL(CREATE_CMS_PAGE, {
          title: selectedPage.title,
          content: selectedPage.content,
        });
        loadPages(); // Reload ALL pages
        setSelectedPage(null); // Clear selection
        setAlertMessage('Page created successfully.');
        setAlertType('success');
      } else {
        // Update existing page
        await fetchGraphQL(UPDATE_CMS_PAGE, {
          id: selectedPage.id,
          title: selectedPage.title,
          content: selectedPage.content,
        });
        loadPages(); // Refresh all pages to get updated data
        setAlertMessage('Page updated successfully.');
        setAlertType('success');
      }
    } catch (err) {
      console.error(err);
      setAlertMessage('Save failed.');
      setAlertType('error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateNew = () => {
    setSelectedPage({
      id: 'new',
      title: '',
      content: '',
    });
  };

  const handleDelete = async () => {
    if (!selectedPage || selectedPage.id === 'new') return;

    if (!window.confirm(`Delete "${selectedPage.title}"?`)) return;

    try {
      await fetchGraphQL(DELETE_CMS_PAGE, { id: selectedPage.id });
      loadPages();
      setSelectedPage(null);
      setAlertMessage('Page deleted successfully.');
      setAlertType('success');
    } catch (err) {
      console.error(err);
      setAlertMessage('Delete failed.');
      setAlertType('error');
    }
  };
  if (isLoading) {
    return <Loading />;
  }
  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#FFFCFB' }}>
      <Sidebar isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />
      <div
        style={{
          flex: 1,
          marginLeft: isSidebarOpen ? '250px' : '60px',
          transition: 'margin-left 0.3s ease',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
        }}
      >
        
        <div className="cms_main_content" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div className="cms_container">
            <h1 className="cms_title">Content Management System</h1>
            
        
            <div className="cms_content_box">
              <div className="cms_sidebar">
                <div className="cms_sidebar_header">
                  <h2>CMS Pages</h2>
                  {/* <button className="cms_new_button" onClick={handleCreateNew}>
                    <i className="fas fa-plus" style={{ marginRight: "8px" }}></i>
                    New Page
                  </button> */}
                </div>
                
                {pages.length === 0 ? (
                  <div className="cms_empty_state">
                    <i className="fas fa-file-alt" style={{ fontSize: "48px", color: "#FFCCBC", marginBottom: "15px" }}></i>
                    <NoData message="No CMS pages found. Create a new page to get started." />
                  </div>
                ) : (
                  <ul className="cms_page_list">
                    {pages.map((p) => (
                      <li 
                        key={p.id} 
                        onClick={() => handleSelect(p)}
                        className={`cms_page_item ${selectedPage?.id === p.id ? 'cms_page_active' : ''}`}
                      >
                        <i className="fas fa-file" style={{ marginRight: "10px", color: "#FF7043" }}></i>
                        <span>{p.title}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="cms_editor">
                  {alertMessage && <AlertMessage message={alertMessage} type={alertType} />}
                {selectedPage ? (
                  <div className="cms_editor_content">
                    <div className="cms_editor_header">
                      <h3>
                        {selectedPage.id === 'new' ? 'Create New Page' : 'Edit Page'}
                      </h3>
                      <div className="cms_editor_actions">
                        <button 
                          className="cms_save_button" 
                          onClick={handleSave} 
                          disabled={isSaving}
                        >
                          {isSaving ? (
                            <i className="fas fa-spinner fa-spin" style={{ marginRight: "8px" }}></i>
                          ) : (
                            <i className="fas fa-save" style={{ marginRight: "8px" }}></i>
                          )}
                          {selectedPage.id === 'new' ? 'Create Page' : 'Save Changes'}
                        </button>
                        {selectedPage.id !== 'new' && (
                          <button
                            className="cms_delete_button"
                            onClick={handleDelete}
                          >
                            <i className="fas fa-trash" style={{ marginRight: "8px" }}></i>
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="cms_form_group">
                      <label>Page Title</label>
                      <input
                        type="text"
                        name="title"
                        value={selectedPage.title}
                        onChange={handleChange}
                        placeholder="Enter page title"
                        className="cms_title_input"
                      />
                    </div>
                    
                    <div className="cms_form_group">
                      <label>Page Content</label>
                      <textarea
                        name="content"
                        value={selectedPage.content}
                        onChange={handleChange}
                        placeholder="Enter page content (supports HTML)"
                        className="cms_content_textarea"
                      />
                    </div>
                    
                    <div className="cms_content_preview">
                      <h4>Preview</h4>
                      <div 
                        className="cms_preview_content"
                        dangerouslySetInnerHTML={{ __html: selectedPage.content || '<p>Enter content to see preview</p>' }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="cms_editor_empty">
                    <i className="fas fa-file-edit" style={{ fontSize: "64px", color: "#FFCCBC", marginBottom: "20px" }}></i>
                    <h3>Select a page to edit</h3>
                    <p>Choose a page from the sidebar or create a new one</p>
                    <button className="cms_new_button" onClick={handleCreateNew}>
                      <i className="fas fa-plus" style={{ marginRight: "8px" }}></i>
                      Create New Page
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CMSManager;